import { useCallback, useEffect, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  downloadCommissionsData,
  getStatementsPeriods
} from 'state/actions/actions';
import buildStatementCarrierOptions from 'helpers/commissions/statementCarrierOptions';

// the statements api lists one period per pay date as 'MM/DD/YYYY', so a month
// holds several of them — the statements view groups them by month and selects
// the whole group, and a month tile has to send that same full group
function findPeriodsForMonth(statementPeriods, monthPeriod) {
  if (typeof monthPeriod !== 'string') return [];
  const [year, month] = monthPeriod.split('-');
  if (!year || !month) return [];
  return statementPeriods.filter(option => {
    const [optionMonth, , optionYear] = String(option?.name ?? '').split('/');
    return Number(optionMonth) === Number(month) && optionYear === year;
  });
}

/**
 * runs the commission statement of a single month for one payee, through the
 * same report the statements view runs; returns `buildMonthDownload`, which
 * hands back a click handler for a 'YYYY-MM' month or undefined when that
 * month has no statement to run
 * @param payeeId - zoho id of the agent the statement is run for
 */
export default function useCommissionMonthDownload(payeeId) {
  const dispatch = useDispatch();
  const statementPeriods = useSelector(
    state => state.appointments.statementPeriods
  );
  const statementsCarriers = useSelector(
    state => state.appointments.satementsCarriersArray
  );
  const isDownloadingReport = useSelector(
    state => state.commissions.isDownloadingReport
  );

  // the home screen can be the first thing the agent lands on, so the period
  // list the statements view relies on is not necessarily loaded yet
  useEffect(() => {
    if (statementPeriods.length === 0) {
      dispatch(getStatementsPeriods());
    }
  }, [statementPeriods.length, dispatch]);

  const carriersOptions = useMemo(
    () => buildStatementCarrierOptions(statementsCarriers),
    [statementsCarriers]
  );

  // the report needs a payee and the carrier list, which only gets loaded for
  // agents whose rank pulls the appointments data
  const canRunReports = Boolean(payeeId) && carriersOptions.length > 1;

  const buildMonthDownload = useCallback(
    monthPeriod => {
      if (!canRunReports) return undefined;
      // a month only has a statement to run once its pay periods are published
      const periods = findPeriodsForMonth(statementPeriods, monthPeriod);
      if (periods.length === 0) return undefined;
      return () =>
        dispatch(
          downloadCommissionsData(
            {
              periods,
              carriers: [],
              agencies: [],
              agents: [{ id: payeeId }],
              includeAllCarriers: true
            },
            carriersOptions
          )
        );
    },
    [canRunReports, dispatch, statementPeriods, payeeId, carriersOptions]
  );

  return { buildMonthDownload, isDownloading: isDownloadingReport };
}
